import React, { useState, useEffect, useContext } from 'react'
import moment from 'moment'
import { Link, useNavigate, useParams } from 'react-router-dom'
import API from '../../../services/API'
import { GlobalContext, UserContext } from '../../../App'
import TipTapReader from '../../../components/TipTap/TipTapReader'
import S3DocumentUploadDisplay from '../../../components/images/S3DocumentUploadDisplay'
import AcknowledgedBy from './AcknowledgedBy'
import AddNew from './AddNew'
import SmallModal from '../../../components/Modals/SmallModal'
import Loading from '../../loading/loadingPage'
import { typeColor } from './announcementConstants'

// @MUI
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import TaskAltIcon from '@mui/icons-material/TaskAlt'

const SingleAnnouncement = () => {
  const { id } = useParams()
  const [announcement, setAnnouncement] = useState(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  const [editMode, setEditMode] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    API.get('/governance/announcement/' + id)
      .then((res) => {
        if (res.status === 200) {
          setAnnouncement(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  const handleAcknowledge = () => {
    API.post(
      '/governance/announcement/mark-as-read?announcementID=' +
        announcement._id,
    ).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Marked as Read' })
        setAnnouncement((prev) => ({
          ...prev,
          isAcknowledgedByCurrentUser: true,
          acknowledgedBy: [
            ...(prev.acknowledgedBy ?? []),
            user?.employee?._id,
          ].filter(Boolean),
        }))
      }
    })
  }

  const handleDelete = () => {
    API.delete('/governance/announcement/' + announcement._id).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Announcement Deleted' })
        navigate('/announcements')
      } else {
        setToast({
          message: 'Unable to delete announcement',
          severity: 'error',
        })
      }
    })
  }

  if (loading) {
    return <Loading />
  }

  if (fetchError) {
    return (
      <Alert severity="error">
        Unable to load Announcement. Please try again later.
      </Alert>
    )
  }

  const canManage =
    (user.employee?.teams ?? []).includes('Governance') ||
    user?.security?.isSiteAdmin

  const needsAck =
    announcement.requiresAcknowledgement &&
    !announcement.isAcknowledgedByCurrentUser

  const accent = typeColor(announcement.type)

  return (
    <Stack gap={1.5}>
      {/* Hero header */}
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          p: { xs: 2, md: 2.5 },
          borderRadius: 2.5,
          border: (t) => `1px solid ${t.palette.divider}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            bgcolor: `${accent}.main`,
          },
        }}
      >
        <Stack gap={1.25}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={1}
          >
            <Stack direction="row" gap={1} alignItems="flex-start" minWidth={0}>
              <Tooltip title="Back to Announcements">
                <IconButton
                  size="small"
                  onClick={() => navigate('/announcements')}
                  sx={{
                    border: (t) => `1px solid ${t.palette.divider}`,
                    borderRadius: 2,
                    mt: 0.25,
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box minWidth={0}>
                <Stack
                  direction="row"
                  gap={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography
                    sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.35rem' } }}
                  >
                    {announcement.title}
                  </Typography>
                  {announcement.type && (
                    <Chip
                      size="small"
                      color={accent}
                      label={announcement.type}
                      sx={{ borderRadius: 999, fontWeight: 600 }}
                    />
                  )}
                  {announcement.isAcknowledgedByCurrentUser &&
                    announcement.requiresAcknowledgement && (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        icon={<CheckCircleIcon />}
                        label="Acknowledged"
                        sx={{ borderRadius: 999, fontWeight: 600 }}
                      />
                    )}
                </Stack>
                <Stack
                  direction="row"
                  gap={0.75}
                  alignItems="center"
                  marginTop={0.5}
                >
                  <Avatar
                    src={announcement?.author?.photograph}
                    sx={{ width: 24, height: 24, fontSize: '0.7rem' }}
                  >
                    {announcement?.author?.firstName?.[0]}
                    {announcement?.author?.lastName?.[0]}
                  </Avatar>
                  <Typography variant="body2" color="text.secondary">
                    {announcement?.author?.firstName}{' '}
                    {announcement?.author?.lastName}
                  </Typography>
                  <Box
                    sx={{
                      width: 3,
                      height: 3,
                      borderRadius: '50%',
                      bgcolor: 'text.disabled',
                    }}
                  />
                  <Tooltip
                    title={moment(announcement.createdAt).format(
                      'Do MMMM YYYY HH:mm',
                    )}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {moment(announcement.createdAt).fromNow()}
                    </Typography>
                  </Tooltip>
                </Stack>
              </Box>
            </Stack>

            {canManage && !editMode && (
              <Stack direction="row" gap={0.5} flexShrink={0}>
                <Tooltip title="Edit Announcement">
                  <IconButton
                    size="small"
                    onClick={() => setEditMode(true)}
                    sx={{
                      border: (t) => `1px solid ${t.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Announcement">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setConfirmDelete(true)}
                    sx={{
                      border: (t) => `1px solid ${t.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Acknowledge CTA */}
      {needsAck && !editMode && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            border: (t) => `1px solid ${t.palette.warning.light}`,
            bgcolor: (t) => `${t.palette.warning.main}14`,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="body2" fontWeight={600}>
              This announcement requires your acknowledgement.
            </Typography>
            <Button
              color="anGreen"
              variant="contained"
              startIcon={<TaskAltIcon />}
              onClick={handleAcknowledge}
              sx={{ borderRadius: 2, textTransform: 'none', flexShrink: 0 }}
            >
              Mark as Read
            </Button>
          </Stack>
        </Paper>
      )}

      {editMode ? (
        <AddNew
          existingAnnouncement={announcement}
          onCancel={() => setEditMode(false)}
        />
      ) : (
        <>
          {/* Content */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 2.5,
              border: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <TipTapReader content={announcement.detail} />
            {announcement.linkToMHRA && (
              <Link to={announcement.linkToMHRA} target="_blank">
                <Alert severity="info" sx={{ marginTop: 1.5, borderRadius: 2 }}>
                  View Safety Alert in full
                </Alert>
              </Link>
            )}
          </Paper>

          {/* Attachments */}
          {announcement.attachments.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 2.5,
                border: (t) => `1px solid ${t.palette.divider}`,
              }}
            >
              <Stack
                direction="row"
                gap={0.75}
                alignItems="center"
                marginBottom={1}
              >
                <AttachFileIcon
                  sx={{ fontSize: '1.1rem', color: 'text.secondary' }}
                />
                <Typography fontWeight={700}>
                  Attachments ({announcement.attachments.length})
                </Typography>
              </Stack>
              <S3DocumentUploadDisplay
                viewOnly
                existingDocuments={announcement.attachments}
              />
            </Paper>
          )}

          {(user.employee?.teams ?? []).includes('Governance') && (
            <AcknowledgedBy
              acknowledgedBy={announcement.acknowledgedBy}
              announcementRequiresAcknowledgement={
                announcement.requiresAcknowledgement
              }
            />
          )}
        </>
      )}

      <SmallModal open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <Stack textAlign="center" alignItems="center" gap={1}>
          <Typography>
            Are you sure you want to delete this Announcement?
          </Typography>
          <ButtonGroup fullWidth>
            <Button color="info" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete}>
              Delete
            </Button>
          </ButtonGroup>
        </Stack>
      </SmallModal>
    </Stack>
  )
}

export default SingleAnnouncement
